package com.tovtalk.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.animateContentSize
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.scaleIn
import androidx.compose.animation.scaleOut
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.gestures.detectVerticalDragGestures
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.MusicNote
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material.icons.outlined.Flag
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import com.tovtalk.utils.strings.StringProvider
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.IntOffset
import kotlin.math.roundToInt
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.extensions.toRelativeTime
import kotlin.time.Instant

/**
 * Shared reel overlay: gradients (responsive % of height), play button, double-tap heart,
 * and bottom row (user + description + music + views | actions).
 * Use same layout for Feed and PostDetail for consistency.
 */
@Composable
fun ReelOverlay(
    modifier: Modifier = Modifier,
    topGradientFraction: Float = 0.15f,
    bottomGradientFraction: Float = 0.25f,
    isPlaying: Boolean,
    showDoubleTapHeart: Boolean,
    avatarUrl: String?,
    username: String,
    displayName: String? = null,
    showFollowButton: Boolean,
    onUserClick: () -> Unit,
    onFollowClick: () -> Unit,
    followButton: @Composable () -> Unit,
    description: String?,
    descriptionExpanded: Boolean = false,
    onDescriptionExpand: () -> Unit = {},
    onDescriptionCollapse: () -> Unit = {},
    musicTitle: String?,
    contentSourceLabel: String? = null,
    viewCountText: String?,
    isLiked: Boolean,
    likeCount: String,
    commentCount: String,
    shareCount: String,
    onLikeClick: () -> Unit,
    onCommentClick: () -> Unit,
    onShareClick: () -> Unit,
    onReportClick: () -> Unit = {},
    isSaved: Boolean = false,
    saveCount: String = "0",
    onSaveClick: () -> Unit = {},
    showMuteButton: Boolean = false,
    isMuted: Boolean = true,
    onMuteClick: () -> Unit = {},
    showLeftColumnInOverlay: Boolean = false,
    onHashtagClick: (String) -> Unit = {},
    commentsEnabled: Boolean = true,
    showReactionIcon: Boolean = true,
    hideActions: Boolean = false,
    createdAt: Instant? = null,
    hashtags: List<String> = emptyList(),
    hasAuthorStory: Boolean = false,
    onAuthorStoryClick: (() -> Unit)? = null,
) {
    BoxWithConstraints(modifier = modifier.fillMaxSize()) {
        val maxHeight = maxHeight
        val topGradientHeight = maxHeight * topGradientFraction
        val bottomGradientHeight = maxHeight * bottomGradientFraction

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(topGradientHeight)
                .align(Alignment.TopCenter)
                .background(
                    brush = Brush.verticalGradient(
                        colors = listOf(
                            TovTalkTheme.colors.videoOverlayGradientTop,
                            Color.Transparent
                        )
                    )
                )
        )
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(bottomGradientHeight)
                .align(Alignment.BottomCenter)
                .background(
                    brush = Brush.verticalGradient(
                        colorStops = arrayOf(
                            0.0f to Color.Transparent,
                            0.3f to TovTalkTheme.colors.videoOverlayGradientBottom.copy(alpha = 0.15f),
                            0.55f to TovTalkTheme.colors.videoOverlayGradientBottom.copy(alpha = 0.35f),
                            0.75f to TovTalkTheme.colors.videoOverlayGradientBottom.copy(alpha = 0.6f),
                            1.0f to TovTalkTheme.colors.videoOverlayGradientBottom
                        )
                    )
                )
        )

        AnimatedVisibility(
            visible = !isPlaying,
            modifier = Modifier.align(Alignment.Center),
            enter = fadeIn() + scaleIn(initialScale = 0.8f),
            exit = fadeOut() + scaleOut(targetScale = 0.8f)
        ) {
            Box(
                modifier = Modifier
                    .size(TovTalkTheme.sizes.videoPlayButtonSize)
                    .clip(CircleShape)
                    .background(TovTalkTheme.colors.overlayDark),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    Icons.Default.PlayArrow,
                    contentDescription = null,
                    modifier = Modifier.size(TovTalkTheme.sizes.videoPlayIconSize),
                    tint = TovTalkTheme.colors.onBrand
                )
            }
        }

        DoubleTapLikeAnimation(
            visible = showDoubleTapHeart,
            modifier = Modifier.align(Alignment.Center)
        )

Row(
            modifier = Modifier
                .fillMaxSize()
                .statusBarsPadding()
                .padding(start = TovTalkTheme.spacing.md, top = TovTalkTheme.spacing.md)
                .padding(end = TovTalkTheme.spacing.xs, bottom = TovTalkTheme.spacing.xxs),
            verticalAlignment = Alignment.Bottom
        ) {
            // Left: in feed, bottom-aligned text/meta area (TikTok-like)
            if (!showLeftColumnInOverlay) {
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxHeight()
                        .padding(end = TovTalkTheme.spacing.md),
                    verticalArrangement = Arrangement.Bottom
                ) {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.xxs),
                        horizontalAlignment = Alignment.End
                    ) {
                        if (!descriptionExpanded) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clip(TovTalkTheme.shapes.full)
                                    .clickable(
                                        interactionSource = remember { MutableInteractionSource() },
                                        indication = null,
                                        onClick = onUserClick
                                    )
                            ) {
                                Text(
                                    text = displayName?.takeIf { it.isNotBlank() } ?: "@$username",
                                    style = TovTalkTheme.typography.titleSmall,
                                    color = TovTalkTheme.colors.onBrand,
                                    textAlign = TextAlign.Right,
                                    modifier = Modifier.fillMaxWidth()
                                )
                            }
                        }
                        val descriptionReservedHeight = with(LocalDensity.current) {
                            // Keep username vertical position stable when description is missing.
                            TovTalkTheme.typography.videoOverlay.lineHeight.toDp() * 2
                        }

                        if (description?.isNotBlank() == true) {
                            description?.let { desc ->
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable(
                                            interactionSource = remember { MutableInteractionSource() },
                                            indication = null,
                                            onClick = onDescriptionExpand
                                        )
                                        .animateContentSize(),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.End
                                ) {
                                    ClickableHashtagText(
                                        text = desc,
                                        style = TovTalkTheme.typography.videoOverlay,
                                        color = TovTalkTheme.colors.onBrand.copy(alpha = TovTalkTheme.opacity.nearFull),
                                        hashtagColor = MaterialTheme.colorScheme.primary,
                                        maxLines = 2,
                                        modifier = Modifier.weight(1f),
                                        onHashtagClick = onHashtagClick,
                                        onClick = onDescriptionExpand,
                                    )
                                }
                            }
                        } else {
                            Spacer(modifier = Modifier.height(descriptionReservedHeight))
                        }
                        if (!musicTitle.isNullOrBlank() && !descriptionExpanded) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.End,
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Text(
                                    text = "♪ $musicTitle",
                                    style = TovTalkTheme.typography.caption,
                                    color = TovTalkTheme.colors.onBrand.copy(alpha = TovTalkTheme.opacity.prominent),
                                    textAlign = TextAlign.End,
                                    maxLines = 1,
                                    modifier = Modifier.weight(1f)
                                )
                            }
                        }
                    }
                }
            }
            // Right/left edge actions column (depends on RTL), no boxed background.
            if (!hideActions) {
                Box(modifier = Modifier.fillMaxHeight()) {
                    Box(
                        modifier = Modifier
                            .align(Alignment.BottomStart)
                            .padding(end = TovTalkTheme.spacing.xxs, bottom = TovTalkTheme.spacing.bottomNavHeight + TovTalkTheme.spacing.xxxs)
                    ) {
                        VideoActionsColumn(
                            isLiked = isLiked,
                            likeCount = likeCount,
                            commentCount = commentCount,
                            shareCount = shareCount,
                            onLikeClick = onLikeClick,
                            onCommentClick = onCommentClick,
                            onShareClick = onShareClick,
                            onReportClick = onReportClick,
                            modifier = Modifier,
                            avatarUrl = avatarUrl,
                            username = if (showLeftColumnInOverlay) username else null,
                            onAvatarClick = onUserClick,
                            showFollowButton = !showLeftColumnInOverlay && showFollowButton,
                            followButton = followButton,
                            isSaved = isSaved,
                            saveCount = saveCount,
                            onSaveClick = onSaveClick,
                            showMuteButton = showMuteButton,
                            isMuted = isMuted,
                            onMuteClick = onMuteClick,
                            orderBottomToTop = !showLeftColumnInOverlay,
                            useLargeIcons = !showLeftColumnInOverlay,
                            commentsEnabled = commentsEnabled,
                            showReactionIcon = showReactionIcon,
                            hasStory = hasAuthorStory,
                            onStoryClick = onAuthorStoryClick,
                        )
                    }
                }
            }
            if (showLeftColumnInOverlay) {
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .padding(end = TovTalkTheme.spacing.md, bottom = TovTalkTheme.spacing.xs)
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier
                            .clip(TovTalkTheme.shapes.full)
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = onUserClick
                            )
                    ) {
                        TovTalkAvatar(
                            imageUrl = avatarUrl,
                            size = TovTalkTheme.sizes.avatarSm,
                            name = username,
                            onClick = onUserClick,
                            hasStory = hasAuthorStory && onAuthorStoryClick != null,
                            onStoryClick = onAuthorStoryClick
                        )
                        if (showFollowButton) {
                            Spacer(modifier = Modifier.width(TovTalkTheme.spacing.sm))
                            followButton()
                        }
                        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.sm))
                        Text(
                            text = "@$username",
                            style = TovTalkTheme.typography.username,
                            color = TovTalkTheme.colors.onBrand
                        )
                    }
                    Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))
                    musicTitle?.let { title ->
                        Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xs))
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                imageVector = Icons.Default.MusicNote,
                                contentDescription = null,
                                modifier = Modifier.size(TovTalkTheme.sizes.iconXs),
                                tint = TovTalkTheme.colors.onBrand.copy(alpha = TovTalkTheme.opacity.heavy)
                            )
                            Spacer(modifier = Modifier.width(TovTalkTheme.spacing.xxs))
                            Text(
                                text = title,
                                style = TovTalkTheme.typography.caption,
                                color = TovTalkTheme.colors.onBrand.copy(alpha = TovTalkTheme.opacity.heavy),
                                maxLines = 1
                            )
                        }
                    }
                    description?.takeIf { it.isNotBlank() }?.let { desc ->
                        Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))
                        var descriptionExpanded by remember { mutableStateOf(false) }
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(TovTalkTheme.shapes.small)
                                .background(
                                    TovTalkTheme.colors.videoOverlayGradientBottom.copy(alpha = TovTalkTheme.opacity.intense)
                                )
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = { descriptionExpanded = !descriptionExpanded }
                                )
                                .animateContentSize()
                                .padding(TovTalkTheme.spacing.xs, TovTalkTheme.spacing.xxs)
                        ) {
                            ClickableHashtagText(
                                text = desc,
                                style = TovTalkTheme.typography.videoOverlay,
                                color = TovTalkTheme.colors.onBrand.copy(alpha = TovTalkTheme.opacity.nearFull),
                                hashtagColor = MaterialTheme.colorScheme.primary,
                                maxLines = if (descriptionExpanded) Int.MAX_VALUE else 1,
                                onHashtagClick = onHashtagClick,
                            )
                        }
                    }
                }
            }
        }



        // TikTok-style description bottom sheet with swipe-to-dismiss
        AnimatedVisibility(
            visible = descriptionExpanded,
            modifier = Modifier.fillMaxSize(),
            enter = fadeIn(),
            exit = fadeOut()
        ) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.4f))
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = onDescriptionCollapse
                    )
            )
        }
        AnimatedVisibility(
            visible = descriptionExpanded,
            modifier = Modifier.align(Alignment.BottomCenter),
            enter = slideInVertically(initialOffsetY = { it }),
            exit = slideOutVertically(targetOffsetY = { it })
        ) {
            var dragOffsetY by remember { mutableStateOf(0f) }
            val dismissThreshold = 120f

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .heightIn(max = maxHeight * 0.65f)
                    .offset { IntOffset(0, dragOffsetY.coerceAtLeast(0f).roundToInt()) }
                    .pointerInput(Unit) {
                        detectVerticalDragGestures(
                            onDragEnd = {
                                if (dragOffsetY > dismissThreshold) {
                                    onDescriptionCollapse()
                                }
                                dragOffsetY = 0f
                            },
                            onDragCancel = { dragOffsetY = 0f },
                            onVerticalDrag = { change, dragAmount ->
                                change.consume()
                                dragOffsetY += dragAmount
                            }
                        )
                    }
                    .background(
                        Color(0xFF1E1E1E),
                        shape = RoundedCornerShape(topStart = 16.dp, topEnd = 16.dp)
                    )
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = {}
                    )
            ) {
                // Drag handle
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 10.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Box(
                        modifier = Modifier
                            .size(width = 36.dp, height = 4.dp)
                            .background(
                                Color.White.copy(alpha = 0.3f),
                                shape = RoundedCornerShape(2.dp)
                            )
                    )
                }
                // Header
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = StringProvider.get("video_description_title"),
                        style = TovTalkTheme.typography.titleSmall,
                        color = Color.White,
                        modifier = Modifier.weight(1f),
                        textAlign = TextAlign.Center
                    )
                }
                // Divider
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(0.5.dp)
                        .background(Color.White.copy(alpha = 0.12f))
                )
                // Content - wraps to actual content size, scrollable only if very long
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .wrapContentHeight()
                        .verticalScroll(rememberScrollState())
                        .padding(horizontal = 16.dp, vertical = 12.dp)
                        .navigationBarsPadding()
                ) {
                    // User row
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier
                            .clip(TovTalkTheme.shapes.full)
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = onUserClick
                            )
                    ) {
                        TovTalkAvatar(
                            imageUrl = avatarUrl,
                            size = TovTalkTheme.sizes.avatarSm,
                            name = username,
                            onClick = onUserClick,
                            hasStory = hasAuthorStory && onAuthorStoryClick != null,
                            onStoryClick = onAuthorStoryClick
                        )
                        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.sm))
                        Column {
                            Text(
                                text = displayName?.takeIf { it.isNotBlank() } ?: username,
                                style = TovTalkTheme.typography.titleSmall,
                                color = Color.White
                            )
                            Text(
                                text = "@$username",
                                style = TovTalkTheme.typography.caption,
                                color = Color.White.copy(alpha = 0.6f)
                            )
                        }
                    }
                    Spacer(modifier = Modifier.height(14.dp))
                    // Description text
                    ClickableHashtagText(
                        text = description.orEmpty(),
                        style = TovTalkTheme.typography.bodyMedium,
                        color = Color.White,
                        hashtagColor = Color(0xFF69C9D0),
                        onHashtagClick = onHashtagClick,
                    )
                    // Hashtags row
                    if (hashtags.isNotEmpty()) {
                        Spacer(modifier = Modifier.height(10.dp))
                        Text(
                            text = hashtags.joinToString(" ") { "#$it" },
                            style = TovTalkTheme.typography.bodyMedium,
                            color = Color(0xFF69C9D0)
                        )
                    }
                    Spacer(modifier = Modifier.height(16.dp))
                    // Meta info row
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        createdAt?.let { date ->
                            Text(
                                text = date.toRelativeTime(),
                                style = TovTalkTheme.typography.caption,
                                color = Color.White.copy(alpha = 0.5f)
                            )
                        }
                        viewCountText?.let { views ->
                            Text(
                                text = views,
                                style = TovTalkTheme.typography.caption,
                                color = Color.White.copy(alpha = 0.5f)
                            )
                        }
                    }
                    // Music
                    if (!musicTitle.isNullOrBlank()) {
                        Spacer(modifier = Modifier.height(12.dp))
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                imageVector = Icons.Default.MusicNote,
                                contentDescription = null,
                                modifier = Modifier.size(16.dp),
                                tint = Color.White.copy(alpha = 0.6f)
                            )
                            Spacer(modifier = Modifier.width(6.dp))
                            Text(
                                text = musicTitle,
                                style = TovTalkTheme.typography.caption,
                                color = Color.White.copy(alpha = 0.6f)
                            )
                        }
                    }
                }
            }
        }
    }
}

/**
 * Content at the bottom of the screen, below the video. Single gray strip with description only;
 * when expanded, text grows upward. Profile/username/follow are on the overlay above the like button.
 */
@Composable
fun ReelBelowVideoContent(
    avatarUrl: String?,
    username: String,
    showFollowButton: Boolean,
    onUserClick: () -> Unit,
    onFollowClick: () -> Unit,
    description: String?
) {
    val stripBackground = TovTalkTheme.colors.surfaceOverlay
    if (description.isNullOrBlank()) return
    val desc = description
    var descriptionExpanded by remember { mutableStateOf(false) }
    var descriptionOverflows by remember { mutableStateOf(false) }
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .wrapContentHeight()
            .background(stripBackground)
            .padding(horizontal = TovTalkTheme.spacing.sm, vertical = TovTalkTheme.spacing.sm)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .align(Alignment.BottomStart)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = { descriptionExpanded = !descriptionExpanded }
                )
                .animateContentSize(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = desc,
                style = TovTalkTheme.typography.bodyMedium,
                color = TovTalkTheme.colors.onBrand,
                maxLines = if (descriptionExpanded) Int.MAX_VALUE else 1,
                modifier = Modifier.weight(1f),
                onTextLayout = { layoutResult ->
                    descriptionOverflows = layoutResult.hasVisualOverflow
                }
            )
            if (!descriptionExpanded && descriptionOverflows) {
                Text(
                    text = " +",
                    style = TovTalkTheme.typography.bodyMedium,
                    color = TovTalkTheme.colors.onBrand.copy(alpha = TovTalkTheme.opacity.heavy)
                )
            }
        }
    }
}
